import React, { useEffect, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { useTheme } from '../contexts/ThemeContext';

const LoadingScreen = () => {
  const loadingRef = useRef(null);
  const textRef = useRef(null);
  const progressRef = useRef(null);
  const dotsRef = useRef(null);
  const { currentColors } = useTheme();

  useEffect(() => {
    // Animate loading text with enhanced effects
    anime({
      targets: textRef.current,
      opacity: [0, 1],
      translateY: [50, 0],
      scale: [0.8, 1],
      duration: 1000,
      easing: 'easeOutElastic(1, .8)'
    });

    // Animate loading dots
    if (dotsRef.current) {
      anime({
        targets: dotsRef.current.children,
        opacity: [0, 1],
        scale: [0, 1],
        duration: 600,
        delay: anime.stagger(200, { start: 400 }),
        easing: 'easeOutBounce'
      });

      // Pulsing animation for dots
      anime({
        targets: dotsRef.current.children,
        scale: [1, 1.2, 1],
        duration: 1000,
        delay: anime.stagger(100, { start: 1000 }),
        loop: true,
        easing: 'easeInOutSine'
      });
    }

    // Animate progress bar with glow effect
    anime({
      targets: progressRef.current,
      width: ['0%', '100%'],
      duration: 2000,
      easing: 'easeOutQuart'
    });

    // Add shimmer effect to progress bar
    anime({
      targets: progressRef.current,
      boxShadow: [
        `0 0 0px ${currentColors.accent}`,
        `0 0 20px ${currentColors.accent}`,
        `0 0 0px ${currentColors.accent}`
      ],
      duration: 1500,
      delay: 500,
      loop: true,
      easing: 'easeInOutSine'
    });

    // Final fade out animation with scale
    const timer = setTimeout(() => {
      anime({
        targets: loadingRef.current,
        opacity: [1, 0],
        scale: [1, 0.9],
        duration: 600,
        easing: 'easeInQuart'
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, [currentColors.accent]);

  return (
    <div 
      ref={loadingRef}
      className="fixed inset-0 flex items-center justify-center z-50 transition-colors duration-500"
      style={{ backgroundColor: currentColors.bg }}
    >
      <div className="text-center">
        <div 
          ref={textRef}
          className="font-mono text-lg font-bold uppercase tracking-widest mb-8 opacity-0"
          style={{ color: currentColors.text }}
        >
          Loading Portfolio
        </div>
        
        {/* Loading Dots */}
        <div ref={dotsRef} className="flex justify-center space-x-2 mb-8">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-full opacity-0"
              style={{ backgroundColor: currentColors.accent }}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div 
          className="w-80 h-1 relative overflow-hidden rounded-full"
          style={{ backgroundColor: currentColors.borderLight }}
        >
          <div 
            ref={progressRef}
            className="absolute top-0 left-0 h-full w-0 rounded-full"
            style={{ backgroundColor: currentColors.accent }}
          />
        </div>
        
        {/* Loading Percentage */}
        <div 
          className="mt-4 font-mono text-xs uppercase tracking-wide"
          style={{ color: currentColors.textSecondary }}
        >
          Initializing Experience...
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;